import { useState, type SubmitEvent } from 'react';
import { AppSheet } from '@/components/ui/app-sheet';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { userLabel } from '@/features/vpn/format';
import { useRegionVpns } from '@/features/vpn/hooks';
import type { FlowLookup, FlowMatch } from '../api';
import { flowColumns } from '../columns';
import { SessionFlows } from '../components/SessionFlows';
import { localInputToUtc, toLocalInput } from '../time';
import { useFlowLookup } from '../hooks';

const matchColumns: ResponsiveTableColumn<FlowMatch>[] = [
  {
    key: 'who',
    label: 'Who',
    hideMobileLabel: true,
    render: (m) => (
      <div className="min-w-0">
        <div className="truncate">{userLabel(m)}</div>
        <div className="text-xs text-muted-foreground truncate">{m.clientDeviceName ?? ''}</div>
      </div>
    ),
  },
  ...flowColumns.map((c) => ({ ...c, render: (m: FlowMatch) => c.render(m.flow) })),
];

/**
 * Who was it: given what a site logged (the gateway's port, a time, perhaps the
 * device's address), the session and so the person (docs/VpnAccess.md, "Slice 5
 * design").
 */
export function VpnLookup() {
  const sites = useRegionVpns();
  const [regionId, setRegionId] = useState('');
  const [address, setAddress] = useState('');
  const [when, setWhen] = useState(() => toLocalInput(new Date()));
  const [destination, setDestination] = useState('');
  const [port, setPort] = useState('');
  const [asked, setAsked] = useState<FlowLookup | null>(null);
  const [open, setOpen] = useState<FlowMatch | null>(null);
  const result = useFlowLookup(asked);

  const at = localInputToUtc(when);
  const portNumber = port.trim() === '' ? undefined : Number(port);
  const portValid = portNumber === undefined || (Number.isInteger(portNumber) && portNumber >= 0 && portNumber <= 65535);
  const ready = !!regionId && !!at && portValid;

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    if (!ready || !at) return;
    setAsked({
      regionId,
      at,
      address: address.trim() || undefined,
      destination: destination.trim() || undefined,
      port: portNumber,
    });
  };

  return (
    <PageContainer width="full">
      <PageHeader
        title="Who was it"
        subtitle="Find the session behind a connection a site logged: the site, when, and the address and port it came from"
      />

      <form className="flex flex-wrap gap-3 mb-4 items-end" onSubmit={submit}>
        <div className="grid gap-1.5">
          <Label htmlFor="lookup-site" className="text-xs">Site</Label>
          <Select value={regionId} onValueChange={setRegionId}>
            <SelectTrigger id="lookup-site" className="w-56">
              <SelectValue placeholder="Choose a site" />
            </SelectTrigger>
            <SelectContent>
              {(sites.data ?? []).map((r) => (
                <SelectItem key={r.regionId} value={r.regionId}>
                  {r.regionName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="lookup-when" className="text-xs">When (your time)</Label>
          <Input id="lookup-when" type="datetime-local" className="w-56" value={when} onChange={(e) => { setWhen(e.target.value); }} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="lookup-destination" className="text-xs">Device address</Label>
          <Input
            id="lookup-destination"
            className="w-44"
            placeholder="Any"
            value={destination}
            onChange={(e) => { setDestination(e.target.value); }}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="lookup-address" className="text-xs">Source address it logged</Label>
          <Input
            id="lookup-address"
            className="w-44"
            placeholder="Any of its gateways"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
            }}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="lookup-port" className="text-xs">Source port it logged</Label>
          <Input
            id="lookup-port"
            className="w-32"
            inputMode="numeric"
            placeholder="Any"
            value={port}
            aria-invalid={!portValid}
            onChange={(e) => { setPort(e.target.value); }}
          />
        </div>
        <Button type="submit" disabled={!ready || result.isFetching}>
          Look up
        </Button>
      </form>
      <p className="text-xs text-muted-foreground mb-4">
        Connections within five minutes of that time match.{at ? ` In UTC: ${at.replace('.000Z', 'Z')}.` : ''}
      </p>

      {asked ? (
        <ResponsiveTable
          columns={matchColumns}
          rows={result.data ?? []}
          rowKey={(m) => `${m.sessionId}:${m.flow.flowId}`}
          isLoading={result.isLoading}
          onRowClick={setOpen}
          empty={
            result.isError ? (
              <EmptyState variant="error" size="inline" title="Couldn't look it up" />
            ) : (
              <EmptyState variant="no-results" size="inline" title="No connection matches" />
            )
          }
        />
      ) : null}

      {open ? (
        <AppSheet
          open
          onOpenChange={(o) => { if (!o) setOpen(null); }}
          size="xl"
          title={`${userLabel(open)}'s session`}
          description={open.clientDeviceName ?? undefined}
        >
          <AppSheet.Body>
            <SessionFlows sessionId={open.sessionId} />
          </AppSheet.Body>
        </AppSheet>
      ) : null}
    </PageContainer>
  );
}
